<template>
  <v-sheet flat border class="pa-4 col-sm-12">
    <p class="text-subtitle-1 pt-3 text-primary mb-3">
      <span class="font-weight-bold">Energy - End Use Power</span>
      - Input values at execution time
    </p>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CurrentDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Current Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.ReadingDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Reading Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.DelayInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Delay in Minutes"
        />
      </v-col>
    </v-row>
    <BusinessHoursInput
      v-model:start-time="localInputData.SiteStartTime"
      v-model:end-time="localInputData.SiteEndTime"
    />
    <v-row>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.ElectricityValueKw"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Site Electricity kW"
        />
      </v-col>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.SolarValueKw"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Site Solar kW"
        />
      </v-col>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.Temperature"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Outside Temperature"
        />
      </v-col>
    </v-row>

    <v-divider class="my-4" />

    <p class="text-subtitle-2 text-primary font-weight-bold mb-3">
      End Use Readings
    </p>

    <p v-if="!endUses.length" class="text-body-2 text-medium-emphasis mb-0">
      No End Uses are available for this site.
    </p>
    <p
      v-else-if="!rows.length"
      class="text-body-2 text-medium-emphasis mb-0"
      data-testid="end-use-rows-empty"
    >
      No End Use values yet. Click "Add input" to enter one reading at a time —
      amps, volts, power factor or kW.
    </p>

    <v-row
      v-for="row in rows"
      :key="row.key"
      density="comfortable"
      align="start"
      data-testid="end-use-row"
    >
      <v-col cols="12" sm="5">
        <v-autocomplete
          v-model="row.endUseId"
          :items="optionsForRow(row)"
          item-title="Label"
          item-value="Id"
          label="End Use"
          variant="outlined"
          density="compact"
          hide-details="auto"
          @update:model-value="emitModel"
        />
      </v-col>
      <v-col cols="5" sm="3">
        <v-text-field
          v-model="row.value"
          :rules="[isNumber]"
          :label="valueLabelFor(row, 'Latest')"
          variant="outlined"
          density="compact"
          hide-details="auto"
          data-testid="end-use-latest"
          @update:model-value="emitModel"
        />
      </v-col>
      <v-col cols="5" sm="3">
        <v-text-field
          v-model="row.prior15"
          :label="valueLabelFor(row, 'Prior 15 min')"
          :disabled="!supportsPrior15(row)"
          :hint="
            supportsPrior15(row)
              ? undefined
              : 'Power factor can only be read as the latest value'
          "
          persistent-hint
          variant="outlined"
          density="compact"
          hide-details="auto"
          data-testid="end-use-prior15"
          @update:model-value="emitModel"
        />
      </v-col>
      <v-col cols="2" sm="1" class="d-flex justify-center">
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          aria-label="Remove input"
          @click="removeInput(row)"
        />
      </v-col>
    </v-row>

    <div class="d-flex mt-4">
      <v-btn
        color="primary"
        variant="tonal"
        density="comfortable"
        prepend-icon="mdi-plus"
        data-testid="add-end-use-input"
        :disabled="!canAddInput"
        @click="addInput"
      >
        Add input
      </v-btn>
    </div>
  </v-sheet>
</template>
<script>
import {
  isNumber,
  isNumberGreaterThanZero,
  hasValue,
  isDate,
} from '@/Core.Patterns/Factory/validationFactory.ts';
import BusinessHoursInput from '@/Core.Service.Domain/Strategies/Strategies/Components/BusinessHoursInput.vue';
import { withBaseInputDefaults } from '@/Core.Service.Domain/Strategies/Strategies/Utils/baseInputDefaults.ts';

// EnergyEndUsePowerV4 does NOT publish a single "EndUses" data source the way v3
// did. It publishes one per unit of measure (see its GetDataSourceNames), and the
// items inside carry only { Id, Name } -- the measure is implied by the key the
// list arrived under. Declaration order is also display order: the grouping the
// ticket asked for (amps, then volts, then power factor, then kW).
// Prior15 mirrors the input: a method that takes no TimeWindow parameter can
// only ever read the latest value, so a prior-15 reading entered for it would be
// shipped and then ignored. Power factor is the only one left -- kW, the volts
// and the amps all take one. Check the method signatures in Core's
// EnergyEndUsePowerV4 before trusting this column; kW gained its TimeWindow
// after this editor was first written and the flag went stale unnoticed.
const END_USE_DATA_SOURCES = [
  { Key: 'EndUsesPhase1Amp', Measure: 'Ph1 Amps', Symbol: 'A', Prior15: true },
  { Key: 'EndUsesPhase2Amp', Measure: 'Ph2 Amps', Symbol: 'A', Prior15: true },
  { Key: 'EndUsesPhase3Amp', Measure: 'Ph3 Amps', Symbol: 'A', Prior15: true },
  { Key: 'EndUsesPhase1Volt', Measure: 'Ph1 Volts', Symbol: 'V', Prior15: true }, // prettier-ignore
  { Key: 'EndUsesPhase2Volt', Measure: 'Ph2 Volts', Symbol: 'V', Prior15: true }, // prettier-ignore
  { Key: 'EndUsesPhase3Volt', Measure: 'Ph3 Volts', Symbol: 'V', Prior15: true }, // prettier-ignore
  { Key: 'EndUsesPowerFactor', Measure: 'PF', Symbol: 'pf%', Prior15: true },
  { Key: 'EndUsesKw', Measure: 'kW', Symbol: 'kW', Prior15: true },
];

const MEASURE_ORDER = new Map(
  END_USE_DATA_SOURCES.map((source, index) => [source.Measure, index])
);

export default {
  name: 'EnergyEndUsePowerV4Input',

  components: { BusinessHoursInput },

  props: {
    InputData: {
      type: Object,
      required: true,
      LatestValues: {
        type: Object,
      },
    },
    DataSources: {
      type: Array,
      required: true,
    },
  },

  data() {
    // A logged execution carries both dictionaries; an End Use may appear in
    // either, so the rows are the union of their keys.
    const latest = { ...(this.InputData?.EndUseLatestValues ?? {}) };
    const prior15 = { ...(this.InputData?.EndUsePrior15Values ?? {}) };
    const rows = [
      ...new Set([...Object.keys(latest), ...Object.keys(prior15)]),
    ].map((endUseId, index) => ({
      key: index,
      endUseId: toEndUseId(endUseId),
      value: latest[endUseId] ?? '',
      prior15: prior15[endUseId] ?? '',
    }));

    return {
      localInputData: withBaseInputDefaults(this.InputData),
      rows,
      nextRowKey: rows.length,
    };
  },

  created() {
    // Recorded values arrive as a plain object, so their order is whatever key
    // ordering JSON gives us. Re-order once to match the picker, so a replayed
    // execution groups its readings by End Use instead of by raw id.
    const order = new Map(
      this.endUseOptions.map((endUse, index) => [endUse.Id, index])
    );
    this.rows.sort(
      (a, b) =>
        (order.get(a.endUseId) ?? Infinity) -
        (order.get(b.endUseId) ?? Infinity)
    );
  },

  computed: {
    // One flat pick list across all eight measure data sources. An End Use has a
    // single unit of measure, so an Id appears in exactly one of them and the
    // ids never collide.
    endUses() {
      return END_USE_DATA_SOURCES.flatMap(({ Key, Measure, Symbol, Prior15 }) =>
        (this.DataSources?.find((ds) => ds.Key === Key)?.Value ?? []).map(
          (endUse) => ({
            Id: endUse.Id,
            Name: endUse.Name,
            Measure,
            Symbol,
            Prior15,
            Label: `${endUse.Name} — ${Measure}`,
          })
        )
      ).sort(byNameThenMeasure);
    },

    // Values recorded against an End Use the site no longer exposes still need
    // to be visible (and editable) when replaying a logged execution.
    endUseOptions() {
      const known = new Set(this.endUses.map((endUse) => endUse.Id));
      const orphans = this.rows
        .map((row) => row.endUseId)
        .filter((id) => hasValue(id) && !known.has(id))
        .map((id) => {
          // A replayed execution carries the names it ran with, so show the real
          // End Use name even when the site no longer exposes it.
          const loggedName = this.localInputData?.EndUseNames?.[id];
          return {
            Id: id,
            Name: loggedName ?? `End Use #${id}`,
            Measure: null,
            Symbol: null,
            Prior15: true,
            Label: loggedName ? `${loggedName} (#${id})` : `End Use #${id}`,
          };
        });

      return [...this.endUses, ...orphans];
    },

    endUseOptionsById() {
      return new Map(this.endUseOptions.map((endUse) => [endUse.Id, endUse]));
    },

    unusedEndUses() {
      const used = new Set(
        this.rows.map((row) => row.endUseId).filter((id) => hasValue(id))
      );
      return this.endUses.filter((endUse) => !used.has(endUse.Id));
    },

    canAddInput() {
      return this.unusedEndUses.length > 0;
    },

    // Every End Use can only be entered once, so each row is offered the ones
    // still free plus its own current selection. Kept as a cached computed --
    // keyed by row -- so typing a value does not rebuild every dropdown.
    optionsByRowKey() {
      return new Map(
        this.rows.map((row) => {
          const options = hasValue(row.endUseId)
            ? [
                ...this.unusedEndUses,
                this.endUseOptionsById.get(row.endUseId),
              ].filter(Boolean)
            : this.unusedEndUses;
          return [row.key, [...options].sort(byNameThenMeasure)];
        })
      );
    },
  },

  watch: {
    localInputData: {
      handler() {
        this.emitModel();
      },
      deep: true,
    },
  },

  methods: {
    isNumber,
    isNumberGreaterThanZero,
    hasValue,
    isDate,

    addInput() {
      this.rows.push({
        key: this.nextRowKey++,
        endUseId: null,
        value: '',
        prior15: '',
      });
    },

    removeInput(row) {
      this.rows = this.rows.filter((r) => r.key !== row.key);
      this.emitModel();
    },

    optionsForRow(row) {
      return this.optionsByRowKey.get(row.key) ?? [];
    },

    valueLabelFor(row, prefix) {
      const symbol = this.endUseOptionsById.get(row.endUseId)?.Symbol;
      return symbol ? `${prefix} (${symbol})` : prefix;
    },

    // Power factor has no TimeWindow parameter on the input, so a prior-15
    // reading for it would be entered and then never read. Unknown (replayed)
    // End Uses stay editable rather than being locked out.
    supportsPrior15(row) {
      const option = this.endUseOptionsById.get(row.endUseId);
      return !option || option.Prior15 !== false;
    },

    emitModel() {
      // A row counts once it has an End Use and at least one real reading. Both
      // values are parsed once here so the dictionaries cannot disagree about
      // which rows counted, and neither carries a reading the user never typed:
      // a missing key reads as 0 on the input, same as an explicit null.
      const entered = this.rows
        .map((row) => ({
          row,
          latest: toDecimal(row.value),
          prior15: toDecimal(row.prior15),
        }))
        .filter(
          ({ row, latest, prior15 }) =>
            hasValue(row.endUseId) && (latest !== null || prior15 !== null)
        );

      this.$emit('update:model', {
        ...this.localInputData,
        EndUseLatestValues: entered.reduce((values, { row, latest }) => {
          if (latest !== null) {
            values[row.endUseId] = latest;
          }
          return values;
        }, {}),
        EndUsePrior15Values: entered.reduce((values, { row, prior15 }) => {
          if (prior15 !== null) {
            values[row.endUseId] = prior15;
          }
          return values;
        }, {}),
        // The "Average Amps" rule method groups End Uses by name, so it needs
        // the names alongside the values -- it throws a KeyNotFoundException
        // without them. Bare End Use name, no unit suffix.
        EndUseNames: entered.reduce((names, { row }) => {
          const name = this.endUseOptionsById.get(row.endUseId)?.Name;
          if (name) {
            names[row.endUseId] = name;
          }
          return names;
        }, {}),
      });
    },
  },
};

// The value field takes free text, so guard what leaves as a reading. Number()
// is used rather than parseFloat() because parseFloat("12abc") silently yields
// 12 -- the field would flag it via the isNumber rule while the payload carried
// it anyway. Infinity (and "1e999", which overflows to it) is rejected too: the
// backend stores these as decimal.
function toDecimal(value) {
  if (!hasValue(value) || String(value).trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// `LatestValues` round-trips through JSON, so its numeric keys come back as
// strings and have to be matched against the numeric End Use ids again.
function toEndUseId(key) {
  const id = Number(key);
  return isNaN(id) ? key : id;
}

// Group a panel's measures together, ordered amps -> volts -> PF -> kW rather
// than alphabetically, which would interleave them.
function byNameThenMeasure(a, b) {
  return (
    a.Name.localeCompare(b.Name) ||
    (MEASURE_ORDER.get(a.Measure) ?? Infinity) -
      (MEASURE_ORDER.get(b.Measure) ?? Infinity)
  );
}
</script>
